import { camelize } from '@mote/helpers/misc/camelize'
import type {
  ColorTones,
  ColorVariants,
  Colors
} from '@xstyled/styled-components'

export const alphaVariants: ColorVariants = [
  4, 6, 8, 16, 24, 36, 48, 64, 80, 92
]

const tones: ColorTones = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900]

export type IAlphaToneMap = Record<number, number>

const alphaToneMap = Array.from({ length: 10 }).reduce(
  (acc: IAlphaToneMap, _, index: number) => {
    acc[alphaVariants[index]] = tones[index]
    return acc as IAlphaToneMap
  },
  {} as IAlphaToneMap
)

export function hexToRGBA(hex: string, opacity = 1): string {
  hex = hex.replace(/[^0-9a-fA-F]/g, '')

  if (hex.length < 5) {
    hex = hex
      .split('')
      .map(s => s + s)
      .join('')
  }

  const m = hex.match(/.{1,2}/g)

  if (!m) return hex

  const rgba = m.map(s => parseInt(s, 16))

  if (opacity > 1 && opacity <= 100) {
    opacity = opacity / 100
  }

  rgba[3] = Number((rgba.length > 3 ? rgba[3] / 255 : opacity).toFixed(2))

  return `rgba(${rgba.join(', ')})`
}

export type AlphaVariants = Record<keyof ColorTones, string>
type ColorAlphaVariant<C extends Colors> = `${Extract<keyof C, string>}Alpha`
type ColorAlphaVariants<C extends Colors> = {
  [K in ColorAlphaVariant<C>]: AlphaVariants
}

export function getHexAlphaVariants<C extends Colors>(
  colors: C
): ColorAlphaVariants<C> {
  return Object.entries(colors).reduce((colorAcc, [colorName, color]) => {
    const alphas = Object.entries(alphaToneMap).reduce(
      (acc: AlphaVariants, [alpha, tone]) => {
        acc[tone] = hexToRGBA(color as string, Number(alpha))
        return acc
      },
      {} as AlphaVariants
    )
    const colorKey = [camelize(colorName), 'Alpha'].join(
      ''
    ) as ColorAlphaVariant<C>

    colorAcc[colorKey] = alphas

    return colorAcc
  }, {} as ColorAlphaVariants<C>)
}
